/**
 * <collapsible-text> — clamps long RTE content to a few lines with a fade and a
 * "View more / View less" toggle. The toggle hides itself automatically when the
 * content is short enough to fit, so it works across products with any
 * description length. No external imports — loaded as a plain module.
 */
class CollapsibleText extends HTMLElement {
  connectedCallback() {
    this.content = this.querySelector('[data-collapsible-content]');
    this.button = this.querySelector('[data-collapsible-toggle]');
    if (!this.content || !this.button) return;

    this.moreLabel = this.dataset.more || 'View more';
    this.lessLabel = this.dataset.less || 'View less';

    this.button.addEventListener('click', this.#toggle);

    // Measure after layout; re-measure once web fonts load (they change height).
    requestAnimationFrame(this.#syncToggle);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(this.#syncToggle).catch(() => {});
    }
    window.addEventListener('resize', this.#syncToggle);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#toggle);
    window.removeEventListener('resize', this.#syncToggle);
  }

  // Show the toggle only when the clamped content actually overflows.
  #syncToggle = () => {
    if (this.classList.contains('is-expanded')) return;
    const overflowing = this.content.scrollHeight > this.content.clientHeight + 2;
    this.button.hidden = !overflowing;
    this.classList.toggle('is-overflowing', overflowing);
  };

  #toggle = () => {
    const expanded = this.classList.toggle('is-expanded');
    this.button.textContent = expanded ? this.lessLabel : this.moreLabel;
    this.button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    if (!expanded) requestAnimationFrame(this.#syncToggle);
  };
}

if (!customElements.get('collapsible-text')) {
  customElements.define('collapsible-text', CollapsibleText);
}
